// src/pages/VolunteerApprovals.js
import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  addDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  orderBy 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const VolunteerApprovals = ({ currentUser }) => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending'); // pending, approved, denied, all
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [bulkSelected, setBulkSelected] = useState([]);
  
  // Checklist states
  const [checklistConfig, setChecklistConfig] = useState([]);
  const [showChecklistConfig, setShowChecklistConfig] = useState(false);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [uploadingFile, setUploadingFile] = useState({});

  // Default checklist items - can be customized per organization
  const defaultChecklistItems = [
    { id: 'background_check', label: 'Background Check Completed', order: 1 },
    { id: 'orientation_attended', label: 'Orientation Session Attended', order: 2 },
    { id: 'training_completed', label: 'Required Training Completed', order: 3 },
    { id: 'id_verified', label: 'Identity Verification', order: 4 },
    { id: 'emergency_form', label: 'Emergency Contact Form', order: 5 },
    { id: 'handbook_received', label: 'Volunteer Handbook Received', order: 6 },
    { id: 'photo_taken', label: 'Photo ID Badge Created', order: 7 },
    { id: 'references_checked', label: 'References Checked', order: 8 }
  ];

  useEffect(() => {
    if (currentUser?.tenantId) {
      loadRegistrations();
      loadChecklistConfig();
    }
  }, [currentUser, filter]);

  const loadChecklistConfig = async () => {
    try {
      // Load organization's checklist configuration
      const configDoc = await getDoc(
        doc(db, 'tenantConfigurations', `${currentUser.tenantId}_volunteer_checklist`)
      );
      
      if (configDoc.exists()) {
        setChecklistConfig(configDoc.data().items || defaultChecklistItems);
      } else {
        // Use default if no config exists
        setChecklistConfig(defaultChecklistItems);
      }
    } catch (err) {
      console.error('Error loading checklist config:', err);
      setChecklistConfig(defaultChecklistItems);
    }
  };

  const saveChecklistConfig = async () => {
    try {
      await setDoc(
        doc(db, 'tenantConfigurations', `${currentUser.tenantId}_volunteer_checklist`),
        {
          items: checklistConfig,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser.email,
          tenantId: currentUser.tenantId
        }
      );
      alert('Checklist configuration saved successfully!');
      setShowChecklistConfig(false);
    } catch (err) {
      console.error('Error saving checklist config:', err);
      alert('Failed to save checklist configuration');
    }
  };

  const loadRegistrations = async () => {
    try {
      setLoading(true);
      setError('');
      
      let q;
      if (filter === 'all') {
        q = query(
          collection(db, 'volunteerRegistrations'),
          where('tenantId', '==', currentUser.tenantId),
          orderBy('submittedAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'volunteerRegistrations'),
          where('tenantId', '==', currentUser.tenantId),
          where('status', '==', filter),
          orderBy('submittedAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      const registrationsData = snapshot.docs.map(doc => {
        const data = doc.data();
        // Initialize checklist if it doesn't exist
        if (!data.onboardingChecklist) {
          data.onboardingChecklist = {};
        }
        return {
          id: doc.id,
          ...data
        };
      });
      
      setRegistrations(registrationsData);
      
    } catch (err) {
      console.error('Error loading registrations:', err);
      setError(`Failed to load volunteer registrations: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const updateChecklistItem = async (registrationId, itemId, field, value) => {
    try {
      const registration = registrations.find(r => r.id === registrationId);
      const currentChecklist = registration.onboardingChecklist || {};
      
      if (!currentChecklist[itemId]) {
        currentChecklist[itemId] = {
          completed: false,
          notes: '',
          fileUrl: '',
          fileName: '',
          updatedAt: null,
          updatedBy: null
        };
      }
      
      currentChecklist[itemId] = {
        ...currentChecklist[itemId],
        [field]: value,
        updatedAt: new Date(),
        updatedBy: currentUser.email
      };
      
      await updateDoc(doc(db, 'volunteerRegistrations', registrationId), {
        onboardingChecklist: currentChecklist
      });
      
      // Update local state
      setRegistrations(prev => prev.map(reg => 
        reg.id === registrationId 
          ? { ...reg, onboardingChecklist: currentChecklist }
          : reg
      ));
      
    } catch (err) {
      console.error('Error updating checklist:', err);
      setError(`Failed to update checklist: ${err.message}`);
    }
  };

  const handleFileUpload = async (registrationId, itemId, file) => {
    if (!file) return;
    
    try {
      setUploadingFile(prev => ({ ...prev, [`${registrationId}-${itemId}`]: true }));
      
      // Create a reference to the file in Firebase Storage
      const fileName = `volunteers/${registrationId}/${itemId}_${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);
      
      // Upload the file
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      // Update the checklist with file info
      await updateChecklistItem(registrationId, itemId, 'fileUrl', downloadURL);
      await updateChecklistItem(registrationId, itemId, 'fileName', file.name);
      
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(`Failed to upload file: ${err.message}`);
    } finally {
      setUploadingFile(prev => ({ ...prev, [`${registrationId}-${itemId}`]: false }));
    }
  };

  const handleApproval = async (registrationId, status, notes = '') => {
    try {
      setProcessing(prev => ({ ...prev, [registrationId]: true }));
      setError('');
      
      const registration = registrations.find(r => r.id === registrationId);
      if (!registration) {
        throw new Error('Registration not found');
      }
      
      // Update registration status with improved data structure
      const updateData = {
        status,
        reviewedAt: serverTimestamp(),
        reviewedBy: currentUser.uid,
        reviewedByEmail: currentUser.email,
        notes: notes || ''
      };
      
      const registrationRef = doc(db, 'volunteerRegistrations', registrationId);
      await updateDoc(registrationRef, updateData);

      // If approved, create volunteer profile with checklist
      if (status === 'approved') {
        await addDoc(collection(db, 'volunteers'), {
          firstName: registration.firstName || 'Unknown',
          lastName: registration.lastName || 'User',
          email: registration.email || '',
          phone: registration.phone || '',
          address: registration.address || '',
          city: registration.city || '',
          state: registration.state || '',
          zip: registration.zip || '',
          emergencyContact: registration.emergencyContact || '',
          emergencyPhone: registration.emergencyPhone || '',
          skills: registration.skills || [],
          interests: registration.interests || [],
          availability: registration.availability || {},
          tenantId: currentUser.tenantId,
          status: 'active',
          joinedAt: serverTimestamp(),
          backgroundCheckStatus: 'pending',
          totalHours: 0,
          additionalInfo: registration.additionalInfo || '',
          registrationId: registrationId,
          createdBy: currentUser.email,
          // Transfer the onboarding checklist
          onboardingChecklist: registration.onboardingChecklist || {}
        });
      }

      // Reload registrations
      await loadRegistrations();
      
      // Clear selection
      setSelectedRegistration(null);
      setBulkSelected(prev => prev.filter(id => id !== registrationId));
      
    } catch (err) {
      console.error('Error processing approval:', err);
      setError(`Failed to process registration: ${err.message}`);
    } finally {
      setProcessing(prev => ({ ...prev, [registrationId]: false }));
    }
  };

  const handleBulkAction = async (action) => {
    if (bulkSelected.length === 0) return;

    const confirmed = window.confirm(
      `Are you sure you want to ${action} ${bulkSelected.length} registration(s)?`
    );

    if (!confirmed) return;

    for (const registrationId of bulkSelected) {
      await handleApproval(registrationId, action);
    }

    setBulkSelected([]);
  };

  const toggleBulkSelection = (registrationId) => {
    setBulkSelected(prev => 
      prev.includes(registrationId)
        ? prev.filter(id => id !== registrationId)
        : [...prev, registrationId]
    );
  };

  const selectAllVisible = () => {
    const pendingIds = registrations
      .filter(reg => filter === 'all' || reg.status === filter)
      .map(reg => reg.id);
    setBulkSelected(pendingIds);
  };

  const clearSelection = () => {
    setBulkSelected([]);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'No date';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'approved': return '#28a745';
      case 'denied': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const addChecklistItem = () => {
    const newItem = {
      id: `custom_${Date.now()}`,
      label: 'New Checklist Item',
      order: checklistConfig.length + 1
    };
    setChecklistConfig([...checklistConfig, newItem]);
  };

  const updateChecklistConfigItem = (index, field, value) => {
    setChecklistConfig(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const removeChecklistConfigItem = (index) => {
    setChecklistConfig(prev => prev.filter((_, i) => i !== index));
  };

  const filteredRegistrations = registrations.filter(reg => 
    filter === 'all' || reg.status === filter
  );

  const calculateChecklistProgress = (checklist) => {
    if (!checklist || Object.keys(checklist).length === 0) return 0;
    
    const totalItems = checklistConfig.length;
    const completedItems = Object.values(checklist).filter(item => item.completed).length;
    
    return totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;
  };

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>Loading registrations...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
          Volunteer Registration Review
        </h1>
        <p style={{ margin: 0, color: '#666', fontSize: '16px' }}>
          Review and approve volunteer registration applications
        </p>
      </div>

      {error && (
        <div style={{ 
          backgroundColor: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Filters and Actions */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <label style={{ fontWeight: 'bold', color: '#333' }}>Filter:</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          >
            <option value="pending">Pending ({registrations.filter(r => r.status === 'pending').length})</option>
            <option value="approved">Approved ({registrations.filter(r => r.status === 'approved').length})</option>
            <option value="denied">Denied ({registrations.filter(r => r.status === 'denied').length})</option>
            <option value="all">All ({registrations.length})</option>
          </select>
          
          <button 
            onClick={loadRegistrations}
            style={{ 
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              background: '#f8f9fa',
              cursor: 'pointer'
            }}
          >
            Refresh
          </button>

          <button 
            onClick={() => setShowChecklistConfig(!showChecklistConfig)}
            style={{ 
              padding: '8px 12px',
              border: '1px solid #17a2b8',
              borderRadius: '4px',
              background: '#17a2b8',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            ⚙️ Configure Checklist
          </button>
        </div>

        {bulkSelected.length > 0 && (
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>
              {bulkSelected.length} selected
            </span>
            <button
              onClick={() => handleBulkAction('approved')}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Approve Selected
            </button>
            <button
              onClick={() => handleBulkAction('denied')}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Deny Selected
            </button>
            <button
              onClick={clearSelection}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Checklist Configuration Modal */}
      {showChecklistConfig && (
        <div style={{
          backgroundColor: 'white',
          border: '2px solid #17a2b8',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 15px 0', color: '#17a2b8' }}>Configure Onboarding Checklist</h3>
          
          <div style={{ marginBottom: '15px' }}>
            {checklistConfig.map((item, index) => (
              <div key={item.id} style={{
                display: 'grid',
                gridTemplateColumns: '40px 1fr 80px',
                gap: '10px',
                alignItems: 'center',
                marginBottom: '10px',
                padding: '10px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                <input
                  type="number"
                  value={item.order}
                  onChange={(e) => updateChecklistConfigItem(index, 'order', parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) => updateChecklistConfigItem(index, 'label', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '5px',
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}
                />
                <button
                  onClick={() => removeChecklistConfigItem(index)}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={addChecklistItem}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              + Add Item
            </button>
            <button
              onClick={saveChecklistConfig}
              style={{
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Save Configuration
            </button>
            <button
              onClick={() => setShowChecklistConfig(false)}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {filteredRegistrations.length > 0 && filter === 'pending' && (
        <div style={{ marginBottom: '20px' }}>
          <button
            onClick={selectAllVisible}
            style={{
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer',
              marginRight: '10px'
            }}
          >
            Select All Visible
          </button>
        </div>
      )}

      {/* Registrations List */}
      {filteredRegistrations.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: 'white', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ fontSize: '18px', color: '#666', marginBottom: '10px' }}>
            No {filter !== 'all' ? filter : ''} registrations found
          </div>
          <div style={{ fontSize: '14px', color: '#999' }}>
            {filter === 'pending' 
              ? 'All registration requests have been reviewed.'
              : 'Try changing the filter to see other registrations.'
            }
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {filteredRegistrations.map(registration => {
            const checklistProgress = calculateChecklistProgress(registration.onboardingChecklist);
            
            return (
              <div key={registration.id} style={{ 
                backgroundColor: 'white', 
                borderRadius: '8px', 
                border: '1px solid #dee2e6',
                overflow: 'hidden',
                cursor: 'pointer'
              }}
              onClick={() => setSelectedRegistration(
                selectedRegistration === registration.id ? null : registration.id
              )}>
                <div style={{ 
                  padding: '20px',
                  borderBottom: selectedRegistration === registration.id ? '1px solid #dee2e6' : 'none'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                        {filter === 'pending' && (
                          <input
                            type="checkbox"
                            checked={bulkSelected.includes(registration.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleBulkSelection(registration.id);
                            }}
                            style={{ marginRight: '12px' }}
                          />
                        )}
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#333' }}>
                          {registration.firstName || 'Unknown'} {registration.lastName || 'User'}
                        </h3>
                        <span style={{
                          marginLeft: '12px',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          backgroundColor: getStatusColor(registration.status),
                          color: 'white',
                          textTransform: 'capitalize'
                        }}>
                          {registration.status}
                        </span>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <strong>Contact:</strong><br />
                          {registration.email || 'No email provided'}<br />
                          {registration.phone || 'No phone provided'}
                        </div>
                        <div>
                          <strong>Submitted:</strong><br />
                          {formatDate(registration.submittedAt)}
                        </div>
                        {registration.reviewedAt && (
                          <div>
                            <strong>Reviewed:</strong><br />
                            {formatDate(registration.reviewedAt)}
                          </div>
                        )}
                      </div>

                      {/* Onboarding Progress Bar */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <strong>Onboarding Progress:</strong>
                          <span style={{ marginLeft: '10px', color: '#666' }}>
                            {checklistProgress}% Complete
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '10px',
                          backgroundColor: '#e9ecef',
                          borderRadius: '5px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${checklistProgress}%`,
                            height: '100%',
                            backgroundColor: checklistProgress === 100 ? '#28a745' : '#17a2b8',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                        <div>
                          <strong>Skills:</strong> {registration.skills?.length > 0 ? (
                            Array.isArray(registration.skills) ? registration.skills.join(', ') : registration.skills
                          ) : 'None listed'}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', marginLeft: '20px' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedRegistration(
                            selectedRegistration === registration.id ? null : registration.id
                          );
                        }}
                        style={{
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer'
                        }}
                      >
                        {selectedRegistration === registration.id ? 'Hide Details' : 'View Details'}
                      </button>

                      {registration.status === 'pending' && (
                        <>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproval(registration.id, 'approved');
                            }}
                            disabled={processing[registration.id]}
                            style={{
                              backgroundColor: '#28a745',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: processing[registration.id] ? 'not-allowed' : 'pointer',
                              opacity: processing[registration.id] ? 0.6 : 1
                            }}
                          >
                            {processing[registration.id] ? 'Processing...' : 'Approve'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproval(registration.id, 'denied');
                            }}
                            disabled={processing[registration.id]}
                            style={{
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              padding: '8px 16px',
                              borderRadius: '4px',
                              fontSize: '14px',
                              cursor: processing[registration.id] ? 'not-allowed' : 'pointer',
                              opacity: processing[registration.id] ? 0.6 : 1
                            }}
                          >
                            {processing[registration.id] ? 'Processing...' : 'Deny'}
                          </button>
                        </>
                      )}

                      {editingChecklist === registration.id ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingChecklist(null);
                          }}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                        >
                          Hide Checklist
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingChecklist(registration.id);
                          }}
                          style={{
                            backgroundColor: '#17a2b8',
                            color: 'white',
                            border: 'none',
                            padding: '8px 16px',
                            borderRadius: '4px',
                            fontSize: '14px',
                            cursor: 'pointer'
                          }}
                        >
                          ✔️ Checklist
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedRegistration === registration.id && (
                    <div style={{ 
                      borderTop: '1px solid #dee2e6', 
                      paddingTop: '16px', 
                      marginTop: '16px',
                      fontSize: '14px'
                    }}>
                      {/* Full Address */}
                      {registration.address && (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Address:</strong><br />
                          {registration.address}<br />
                          {registration.city && registration.state && registration.zip && 
                            `${registration.city}, ${registration.state} ${registration.zip}`
                          }
                        </div>
                      )}

                      {/* Emergency Contact */}
                      {registration.emergencyContact && (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Emergency Contact:</strong><br />
                          {registration.emergencyContact} - {registration.emergencyPhone || 'No phone provided'}
                        </div>
                      )}

                      {/* All Skills */}
                      {registration.skills && registration.skills.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>All Skills:</strong><br />
                          {Array.isArray(registration.skills) ? registration.skills.join(', ') : registration.skills}
                        </div>
                      )}

                      {/* Interests */}
                      {registration.interests && registration.interests.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Interests:</strong><br />
                          {Array.isArray(registration.interests) ? registration.interests.join(', ') : registration.interests}
                        </div>
                      )}

                      {/* Availability */}
                      <div style={{ marginBottom: '12px' }}>
                        <strong>Availability:</strong><br />
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {registration.availability ? 
                            Object.entries(registration.availability).map(([day, times]) => {
                              const availableTimes = Object.entries(times || {})
                                .filter(([time, available]) => available)
                                .map(([time]) => time);
                              
                              if (availableTimes.length > 0) {
                                return `${day.charAt(0).toUpperCase() + day.slice(1)}: ${availableTimes.join(', ')}`;
                              }
                              return null;
                            }).filter(Boolean).join(' | ') || 'No availability specified'
                            : 'No availability specified'
                          }
                        </div>
                      </div>

                      {/* Additional Info */}
                      {registration.additionalInfo && (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Additional Info:</strong><br />
                          <div style={{ 
                            padding: '8px', 
                            backgroundColor: '#f8f9fa', 
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#555',
                            marginTop: '4px'
                          }}>
                            {registration.additionalInfo}
                          </div>
                        </div>
                      )}

                      {/* Referral Source */}
                      {registration.referralSource && (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>How they heard about us:</strong> {registration.referralSource}
                        </div>
                      )}

                      {/* Admin Notes */}
                      {registration.notes && (
                        <div style={{ marginBottom: '12px' }}>
                          <strong>Review Notes:</strong><br />
                          <div style={{ 
                            padding: '8px', 
                            backgroundColor: '#fff3cd', 
                            borderRadius: '4px',
                            fontSize: '13px',
                            color: '#856404',
                            marginTop: '4px'
                          }}>
                            {registration.notes}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Onboarding Checklist */}
                  {editingChecklist === registration.id && (
                    <div style={{
                      borderTop: '2px solid #17a2b8',
                      paddingTop: '20px',
                      marginTop: '20px'
                    }}>
                      <h4 style={{ margin: '0 0 15px 0', color: '#17a2b8' }}>
                        📋 Onboarding Checklist
                      </h4>
                      
                      {checklistConfig.sort((a, b) => a.order - b.order).map(item => {
                        const checklistData = registration.onboardingChecklist?.[item.id] || {};
                        const isUploading = uploadingFile[`${registration.id}-${item.id}`];
                        
                        return (
                          <div key={item.id} style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '15px',
                            marginBottom: '15px',
                            border: checklistData.completed ? '2px solid #28a745' : '1px solid #dee2e6'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
                              {/* Checkbox */}
                              <input
                                type="checkbox"
                                checked={checklistData.completed || false}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  updateChecklistItem(registration.id, item.id, 'completed', e.target.checked);
                                }}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  cursor: 'pointer',
                                  marginTop: '2px'
                                }}
                              />
                              
                              {/* Content */}
                              <div style={{ flex: 1 }}>
                                <label style={{
                                  fontSize: '16px',
                                  fontWeight: checklistData.completed ? 'bold' : 'normal',
                                  color: checklistData.completed ? '#28a745' : '#333',
                                  textDecoration: checklistData.completed ? 'line-through' : 'none',
                                  cursor: 'pointer'
                                }}>
                                  {item.label}
                                </label>
                                
                                {/* File Upload */}
                                <div style={{ marginTop: '10px' }}>
                                  {checklistData.fileUrl ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      <span style={{ fontSize: '14px', color: '#28a745' }}>
                                        ✓ File uploaded: {checklistData.fileName}
                                      </span>
                                      <a 
                                        href={checklistData.fileUrl} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ 
                                          color: '#007bff', 
                                          textDecoration: 'underline',
                                          fontSize: '14px'
                                        }}
                                      >
                                        View
                                      </a>
                                    </div>
                                  ) : (
                                    <input
                                      type="file"
                                      disabled={isUploading}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        if (e.target.files[0]) {
                                          handleFileUpload(registration.id, item.id, e.target.files[0]);
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ fontSize: '14px' }}
                                    />
                                  )}
                                  {isUploading && (
                                    <span style={{ fontSize: '14px', color: '#6c757d' }}>
                                      Uploading...
                                    </span>
                                  )}
                                </div>
                                
                                {/* Notes */}
                                <div style={{ marginTop: '10px' }}>
                                  <textarea
                                    placeholder="Add notes..."
                                    value={checklistData.notes || ''}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      updateChecklistItem(registration.id, item.id, 'notes', e.target.value);
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      width: '100%',
                                      minHeight: '60px',
                                      padding: '8px',
                                      border: '1px solid #ddd',
                                      borderRadius: '4px',
                                      fontSize: '14px',
                                      resize: 'vertical'
                                    }}
                                  />
                                </div>
                                
                                {/* Last Updated */}
                                {checklistData.updatedAt && (
                                  <div style={{ 
                                    marginTop: '8px', 
                                    fontSize: '12px', 
                                    color: '#666',
                                    fontStyle: 'italic'
                                  }}>
                                    Last updated: {formatDate(checklistData.updatedAt)} by {checklistData.updatedBy}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Click to expand hint */}
                <div style={{ 
                  textAlign: 'center', 
                  fontSize: '12px', 
                  color: '#999', 
                  padding: '8px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #f0f0f0'
                }}>
                  {selectedRegistration === registration.id ? 'Click to collapse details' : 'Click to view full details'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default VolunteerApprovals;